import React, { useState, useEffect } from 'react';
import { getPendingKYC, approveKYC, rejectKYC } from '../../services/api';
import toast from 'react-hot-toast';
import { CheckCircleIcon, XCircleIcon, EyeIcon } from '@heroicons/react/24/outline';

const KYCReview = () => {
  const [pendingKYC, setPendingKYC] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKYC, setSelectedKYC] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  useEffect(() => {
    fetchPendingKYC();
  }, []);

  const fetchPendingKYC = async () => {
    try {
      const response = await getPendingKYC();
      if (response.data.success) {
        setPendingKYC(response.data.pending);
      }
    } catch (error) {
      toast.error('Failed to fetch pending KYC');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      const response = await approveKYC(userId);
      if (response.data.success) {
        toast.success('KYC approved successfully');
        fetchPendingKYC();
      }
    } catch (error) {
      toast.error('Failed to approve KYC');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      const response = await rejectKYC(selectedKYC.user_id, rejectReason);
      if (response.data.success) {
        toast.success('KYC rejected');
        setShowRejectModal(false);
        setRejectReason('');
        setSelectedKYC(null);
        fetchPendingKYC();
      }
    } catch (error) {
      toast.error('Failed to reject KYC');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-gray-400">Loading KYC submissions...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-page fade-in">
      <div className="dashboard-head">
        <h1 className="dashboard-page-title">KYC Verification Review</h1>
        <p className="dashboard-section-subtitle">Review identity submissions and take action</p>
      </div>

      {pendingKYC.length === 0 ? (
        <section className="card dashboard-section">
          <div className="dashboard-section-body">
            <div className="styled-empty-state">
              <div className="empty-state-icon">
                <svg
                  className="h-8 w-8 flex-none text-emerald-400"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="styled-empty-title">All Caught Up!</p>
              <p className="styled-empty-subtitle">No pending KYC submissions to review.</p>
            </div>
          </div>
        </section>
      ) : (
        <div className="kyc-review-stack">
          {pendingKYC.map((kyc) => (
            <section key={kyc.id} className="card dashboard-section">
              <div className="dashboard-section-body">
                <div className="kyc-review-top">
                  <div>
                    <h3 className="kyc-review-name">{kyc.full_name}</h3>
                    <p className="kyc-review-contact">{kyc.email || kyc.phone}</p>
                  </div>

                  <div className="kyc-review-doc-actions">
                    <button
                      onClick={() =>
                        window.open(
                          `http://localhost:5000/uploads/kyc/id_documents/${kyc.id_document_path?.split('\\').pop()}`,
                          '_blank'
                        )
                      }
                      className="admin-action-btn"
                      type="button"
                    >
                      <EyeIcon className="w-4 h-4" />
                      View ID
                    </button>

                    <button
                      onClick={() =>
                        window.open(
                          `http://localhost:5000/uploads/kyc/selfies/${kyc.selfie_path?.split('\\').pop()}`,
                          '_blank'
                        )
                      }
                      className="admin-action-btn"
                      type="button"
                    >
                      <EyeIcon className="w-4 h-4" />
                      View Selfie
                    </button>
                  </div>
                </div>

                <div className="kyc-review-meta-grid">
                  <div className="kyc-review-meta-card">
                    <p className="kyc-review-meta-label">Country</p>
                    <p className="kyc-review-meta-value">{kyc.country}</p>
                  </div>

                  <div className="kyc-review-meta-card">
                    <p className="kyc-review-meta-label">ID Type</p>
                    <p className="kyc-review-meta-value capitalize">
                      {kyc.id_type?.replace('_', ' ')}
                    </p>
                  </div>

                  <div className="kyc-review-meta-card">
                    <p className="kyc-review-meta-label">Date of Birth</p>
                    <p className="kyc-review-meta-value">
                      {new Date(kyc.date_of_birth).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="kyc-review-meta-card">
                    <p className="kyc-review-meta-label">Submitted</p>
                    <p className="kyc-review-meta-value">
                      {new Date(kyc.submitted_at).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="kyc-review-action-row">
                  <button
                    onClick={() => handleApprove(kyc.user_id)}
                    className="kyc-action-btn approve"
                    type="button"
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                    Approve
                  </button>

                  <button
                    onClick={() => {
                      setSelectedKYC(kyc);
                      setShowRejectModal(true);
                    }}
                    className="kyc-action-btn reject"
                    type="button"
                  >
                    <XCircleIcon className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              </div>
            </section>
          ))}
        </div>
      )}

      {showRejectModal && (
        <div className="kyc-modal-overlay">
          <div className="kyc-modal-card">
            <h3 className="kyc-modal-title">Reject KYC</h3>
            <p className="kyc-modal-subtitle">User: {selectedKYC?.full_name}</p>

            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="auth-input kyc-modal-textarea"
              placeholder="Reason for rejection..."
            />

            <div className="kyc-modal-actions">
              <button
                onClick={handleReject}
                className="kyc-action-btn reject"
                type="button"
              >
                Confirm Reject
              </button>

              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                  setSelectedKYC(null);
                }}
                className="admin-action-btn"
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYCReview;